import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, AlertCircle, ArrowLeft, RefreshCw, Bug } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  signInWithPopup,
  OAuthProvider,
  onAuthStateChanged,
} from 'firebase/auth';
import { auth } from '../firebase';
import { LogoWithText } from '@/components/Logo';
import { GrainOverlay, Glow } from '@/components/landing/LandingShell';
import { isNativePlatform, signInWithAppleNative } from '../lib/appleNative';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Debug mode - set to true to show debug logs on screen
const DEBUG_MODE = true;

export default function AppleAuth({ onLogin }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('Ready to sign in');
  const [showDebug, setShowDebug] = useState(DEBUG_MODE);
  const [debugLogs, setDebugLogs] = useState([]);
  const debugRef = useRef(null);

  // Add debug log entry
  const addLog = useCallback((message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    // Stable unique id so React keys are not tied to array index
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const entry = { id, timestamp, message, type };
    setDebugLogs(prev => [...prev.slice(-50), entry]); // Keep last 50 logs
    console.log(`[${type.toUpperCase()}] ${message}`);
    
    // Auto-scroll debug panel
    setTimeout(() => {
      if (debugRef.current) {
        debugRef.current.scrollTop = debugRef.current.scrollHeight;
      }
    }, 100);
  }, []);

  // Handle successful authentication with our backend
  const handleAuthSuccess = useCallback(async (user) => {
    try {
      addLog(`Processing user: ${user.email || user.uid}`, 'success');
      setStatus('Completing sign-in...');
      
      addLog('Getting Firebase ID token...', 'info');
      const firebaseToken = await user.getIdToken();
      addLog(`Got token (length: ${firebaseToken.length})`, 'success');
      
      addLog(`Sending to backend: ${API}/auth/firebase`, 'info');
      const response = await axios.post(`${API}/auth/firebase`, {
        firebase_token: firebaseToken,
        provider: 'apple'
      });
      
      addLog(`Backend response: ${JSON.stringify(response.data).substring(0, 100)}...`, 'success');
      
      if (response.data.token) {
        addLog('Login successful! Navigating to onboarding...', 'success');
        toast.success('Signed in with Apple successfully!');
        onLogin(response.data.token);
        navigate('/onboarding');
        return true;
      }
      addLog('No token in response', 'error');
      return false;
    } catch (err) {
      addLog(`Backend error: ${err.message}`, 'error');
      addLog(`Error details: ${JSON.stringify(err.response?.data || {})}`, 'error');
      const errorMessage = err.response?.data?.detail || err.message || 'Authentication failed';
      setError(errorMessage);
      toast.error(errorMessage);
      setLoading(false);
      return false;
    }
  }, [navigate, onLogin, addLog]);

  // Listen for auth state changes
  useEffect(() => {
    addLog('Setting up onAuthStateChanged listener...', 'info');
    
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      addLog(`Auth state changed: ${user ? `User found (${user.email || user.uid})` : 'No user'}`, 'info');
      if (user && loading) {
        addLog('User detected while loading, processing...', 'info');
        await handleAuthSuccess(user);
      }
    });

    return () => {
      addLog('Cleaning up auth listener', 'info');
      unsubscribe();
    };
  }, [loading, handleAuthSuccess, addLog]);

  // Check for existing user on mount
  useEffect(() => {
    addLog('Component mounted', 'info');
    addLog(`Current URL: ${window.location.href}`, 'info');
    addLog(`User Agent: ${navigator.userAgent.substring(0, 100)}...`, 'info');
    
    const currentUser = auth.currentUser;
    addLog(`Checking for existing user: ${currentUser ? 'Found' : 'None'}`, 'info');
    
    if (currentUser) {
      addLog(`Existing user: ${currentUser.email || currentUser.uid}`, 'success');
      setLoading(true);
      handleAuthSuccess(currentUser);
    }
  }, [handleAuthSuccess, addLog]);

  // Get user-friendly error message
  const getErrorMessage = (err) => {
    const code = err.code || '';
    addLog(`Error code: ${code}`, 'error');
    addLog(`Error message: ${err.message}`, 'error');
    
    if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
      return 'Sign-in was cancelled. Please try again.';
    }
    if (code === 'auth/popup-blocked') {
      return 'Popup was blocked. Please allow popups and try again.';
    }
    if (code === 'auth/unauthorized-domain') {
      return 'This domain is not authorized. Please contact support.';
    }
    if (code === 'auth/operation-not-allowed') {
      return 'Apple Sign-In is not enabled. Please contact support.';
    }
    if (code === 'auth/network-request-failed') {
      return 'Network error. Please check your connection.';
    }
    if (code === 'auth/internal-error') {
      return 'An internal error occurred. Please try again.';
    }
    if (err.message?.includes('popup')) {
      return 'Sign-in popup issue. Please try again.';
    }
    
    return err.message || 'Sign-in failed. Please try again.';
  };

  // Initiate Apple Sign-In
  const initiateAppleSignIn = async () => {
    addLog('=== STARTING APPLE SIGN-IN ===', 'info');
    addLog(`Timestamp: ${new Date().toISOString()}`, 'info');

    setLoading(true);
    setError(null);
    setStatus('Opening Apple Sign-In...');

    // Native iOS (Capacitor): use the native Apple Sign-In plugin.
    if (isNativePlatform()) {
      addLog('Native platform detected — using native Apple Sign-In plugin', 'info');
      try {
        const data = await signInWithAppleNative();
        addLog('Native Apple Sign-In success', 'success');
        if (data.token) {
          toast.success('Signed in with Apple successfully!');
          onLogin(data.token);
          navigate(data.is_new_user ? '/onboarding' : '/zenflow');
        }
      } catch (err) {
        addLog(`Native Apple Sign-In error: ${err.message}`, 'error');
        setError(getErrorMessage(err));
        setLoading(false);
      }
      return;
    }

    try {
      addLog('Creating OAuthProvider for apple.com...', 'info');
      const provider = new OAuthProvider('apple.com');
      provider.addScope('email');
      provider.addScope('name');
      addLog('Provider configured with scopes: email, name', 'info');

      addLog('Calling signInWithPopup...', 'info');
      addLog(`Auth object exists: ${!!auth}`, 'info');
      addLog(`Auth currentUser before: ${auth.currentUser?.email || 'none'}`, 'info');
      
      const result = await signInWithPopup(auth, provider);
      
      addLog('signInWithPopup completed!', 'success');
      addLog(`Result exists: ${!!result}`, 'info');
      addLog(`User exists: ${!!result?.user}`, 'info');
      
      if (result && result.user) {
        addLog(`User email: ${result.user.email}`, 'success');
        addLog(`User UID: ${result.user.uid}`, 'success');
        addLog(`Provider ID: ${result.providerId}`, 'info');
        await handleAuthSuccess(result.user);
      } else {
        addLog('No user in result!', 'error');
        throw new Error('No user returned from Apple Sign-In');
      }
    } catch (err) {
      addLog('=== SIGN-IN ERROR ===', 'error');
      addLog(`Error type: ${err.constructor.name}`, 'error');
      addLog(`Error code: ${err.code || 'none'}`, 'error');
      addLog(`Error message: ${err.message}`, 'error');
      addLog(`Error stack: ${err.stack?.substring(0, 200) || 'none'}`, 'error');
      
      setError(getErrorMessage(err));
      setLoading(false);
      
      if (err.code !== 'auth/popup-closed-by-user' && err.code !== 'auth/cancelled-popup-request') {
        toast.error('Apple Sign-In failed. Please try again.');
      }
    }
  };

  const handleRetry = () => {
    addLog('Retry button clicked', 'info');
    setError(null);
    initiateAppleSignIn();
  };

  const handleGoBack = () => {
    navigate('/auth');
  };

  const clearLogs = () => {
    setDebugLogs([]);
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 py-6 sm:py-8 relative overflow-x-hidden bg-[#0A1514] text-white"
      style={{ fontFamily: "'Manrope', sans-serif" }}
    >
      <GrainOverlay />
      <Glow className="-top-32 -left-32 w-[520px] h-[520px]" color="#1C4749" opacity={0.18} />
      <Glow className="top-1/2 -right-40 w-[420px] h-[420px]" color="#FFD166" opacity={0.06} />

      {/* Debug Toggle Button */}
      <button
        onClick={() => setShowDebug(!showDebug)}
        className="fixed top-4 right-4 z-50 p-2 bg-gray-800 text-white rounded-full shadow-lg"
      >
        <Bug className="w-5 h-5" />
      </button>

      {/* Debug Panel */}
      {showDebug && (
        <div className="fixed top-16 right-4 left-4 md:left-auto md:w-96 z-40 bg-gray-900 text-green-400 rounded-lg shadow-xl border border-gray-700 max-h-64 overflow-hidden">
          <div className="flex justify-between items-center px-3 py-2 bg-gray-800 border-b border-gray-700">
            <span className="text-xs font-mono font-bold">DEBUG LOG</span>
            <button onClick={clearLogs} className="text-xs text-gray-400 hover:text-white">Clear</button>
          </div>
          <div ref={debugRef} className="p-2 overflow-y-auto max-h-52 font-mono text-xs">
            {debugLogs.length === 0 ? (
              <p className="text-gray-500">No logs yet...</p>
            ) : (
              debugLogs.map((log) => (
                <div key={log.id} className={`mb-1 ${
                  log.type === 'error' ? 'text-red-400' : 
                  log.type === 'success' ? 'text-green-400' : 
                  'text-gray-300'
                }`}>
                  <span className="text-gray-500">[{log.timestamp}]</span> {log.message}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm text-center relative z-10"
      >
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-5">
          <LogoWithText iconSize="w-8 h-8" textSize="text-2xl" />
        </div>

        {/* Content Card */}
        <div className="bg-card/60 backdrop-blur-xl border border-border rounded-3xl p-6 shadow-lg">
          {error ? (
            // Error State
            <div>
              <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-destructive" />
              </div>
              <h2 className="text-xl font-heading font-bold text-foreground mb-2">
                Sign-In Failed
              </h2>
              <p className="text-sm text-muted-foreground mb-6">
                {error}
              </p>
              <div className="space-y-3">
                <Button
                  onClick={handleRetry}
                  className="w-full rounded-full bg-black text-white hover:bg-gray-800"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Try Again with Apple
                </Button>
                <Button
                  variant="outline"
                  onClick={handleGoBack}
                  className="w-full rounded-full"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Other Sign-In Options
                </Button>
              </div>
            </div>
          ) : loading ? (
            // Loading State
            <div>
              <div className="w-16 h-16 rounded-full bg-black flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.09l-.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
              </div>
              <h2 className="text-xl font-heading font-bold text-foreground mb-2">
                Apple Sign-In
              </h2>
              <p className="text-sm text-muted-foreground mb-4">
                {status}
              </p>
              <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto mb-4" />
              <p className="text-xs text-muted-foreground">
                Complete sign-in in the Apple window
              </p>
            </div>
          ) : (
            // Ready State
            <div>
              <div className="w-16 h-16 rounded-full bg-black flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.09l-.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
              </div>
              <h2 className="text-xl font-heading font-bold text-foreground mb-2">
                Apple Sign-In
              </h2>
              <p className="text-sm text-muted-foreground mb-6">
                Sign in securely with your Apple ID
              </p>
              
              <Button
                onClick={initiateAppleSignIn}
                className="w-full rounded-full bg-black text-white hover:bg-gray-800 h-12 text-base font-medium"
              >
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.09l-.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
                Continue with Apple
              </Button>
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="text-xs text-muted-foreground mt-6">
          By signing in, you agree to our Terms of Service and Privacy Policy
        </p>

        <button
          onClick={handleGoBack}
          className="mt-4 text-sm text-primary hover:text-primary/80 transition-colors font-medium inline-flex items-center gap-1"
        >
          <ArrowLeft className="w-3.5 h-3.5" aria-hidden />
          Use email instead
        </button>
        
        {/* Version indicator */}
        <p className="text-xs text-muted-foreground mt-4 opacity-50">v1.1.1-debug</p>
      </motion.div>
    </div>
  );
}
